import React, { useRef, useState } from 'react';
import axios from 'axios';
import { Paperclip, X, Upload, AlertCircle } from 'lucide-react';
import { Button } from './ui/button';

const API_URL = process.env.REACT_APP_BACKEND_URL
  ? process.env.REACT_APP_BACKEND_URL.replace(/\/+$/, '') + '/api'
  : '/api';


const MAX_SIZE = 100 * 1024 * 1024; // 100 MB

/**
 * FileUpload — staged file selector with optional immediate upload.
 *
 * Modes:
 *  - staged (leadId not provided, or uploadAfterCreate=true):
 *      Collect files locally; parent calls uploadPending(leadId, entityType, entityId) after entity creation.
 *  - immediate (leadId provided, uploadAfterCreate=false):
 *      Upload each file immediately on selection.
 *
 * Props:
 *  leadId          — lead UUID (required for immediate mode)
 *  entityType      — 'interaction' | 'note' | 'task' | 'general'  (default: 'general')
 *  entityId        — UUID of the parent entity (interaction/note/task)
 *  uploadAfterCreate — if true, hold files and expose uploadPending via ref
 *  onUploaded      — callback(uploadedFiles[]) after successful upload(s)
 *  fileListRef     — if provided, keeps a ref to the staged file list so parent can read it
 *  compact         — render a compact attach-button instead of a drop zone
 */
const FileUpload = React.forwardRef(function FileUpload(
  {
    leadId,
    entityType = 'general',
    entityId,
    uploadAfterCreate = false,
    onUploaded,
    compact = false,
  },
  ref
) {
  const inputRef = useRef(null);
  const [stagedFiles, setStagedFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [errors, setErrors] = useState([]);

  // Expose uploadPending to parent via ref
  React.useImperativeHandle(ref, () => ({
    async uploadPending(resolvedLeadId, resolvedEntityType, resolvedEntityId) {
      if (stagedFiles.length === 0) return [];
      return uploadFiles(stagedFiles, resolvedLeadId, resolvedEntityType, resolvedEntityId);
    },
    hasPending() {
      return stagedFiles.length > 0;
    },
    clear() {
      setStagedFiles([]);
      setErrors([]);
    },
  }));

  function validateFile(file) {
    if (file.size > MAX_SIZE) return `${file.name}: exceeds ${MAX_SIZE / 1024 / 1024} MB limit`;
    return null;
  }

  function handleFiles(fileList) {
    const newErrors = [];
    const valid = [];
    Array.from(fileList).forEach((f) => {
      const err = validateFile(f);
      if (err) newErrors.push(err);
      else valid.push(f);
    });
    setErrors(newErrors);
    if (!valid.length) return;

    if (uploadAfterCreate || !leadId) {
      setStagedFiles((prev) => [...prev, ...valid]);
    } else {
      uploadFiles(valid, leadId, entityType, entityId);
    }
  }

  async function uploadFiles(files, resolvedLeadId, resolvedEntityType, resolvedEntityId) {
    setUploading(true);
    const uploaded = [];
    try {
      for (const file of files) {
        const formData = new FormData();
        formData.append('file', file);
        formData.append('entity_type', resolvedEntityType || 'general');
        if (resolvedEntityId) formData.append('entity_id', resolvedEntityId);

        const res = await axios.post(
          `${API_URL}/leads/${resolvedLeadId}/files`,
          formData
        );
        uploaded.push(res.data);
      }
      setStagedFiles([]);
      if (onUploaded) onUploaded(uploaded);
    } catch (err) {
      const msg = err.response?.data?.detail || 'Upload failed';
      setErrors((prev) => [...prev, msg]);
    } finally {
      setUploading(false);
    }
    return uploaded;
  }

  function removeStagedFile(idx) {
    setStagedFiles((prev) => prev.filter((_, i) => i !== idx));
  }

  function onDrop(e) {
    e.preventDefault();
    handleFiles(e.dataTransfer.files);
  }

  if (compact) {
    return (
      <div className="space-y-1">
        <input
          ref={inputRef}
          type="file"
          multiple

          className="hidden"
          onChange={(e) => handleFiles(e.target.files)}
        />
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={() => inputRef.current?.click()}
          disabled={uploading}
          className="text-slate-600"
        >
          <Paperclip className="w-4 h-4 mr-1" />
          Attach Files
          {stagedFiles.length > 0 && (
            <span className="ml-1 bg-blue-100 text-blue-700 rounded-full px-1.5 text-xs">
              {stagedFiles.length}
            </span>
          )}
        </Button>

        {stagedFiles.length > 0 && (
          <ul className="space-y-1 mt-1">
            {stagedFiles.map((f, i) => (
              <li key={i} className="flex items-center gap-2 text-xs text-slate-600 bg-slate-50 rounded px-2 py-1">
                <Paperclip className="w-3 h-3 shrink-0" />
                <span className="truncate max-w-[200px]">{f.name}</span>
                <span className="text-slate-400 shrink-0">({(f.size / 1024).toFixed(0)} KB)</span>
                <button type="button" onClick={() => removeStagedFile(i)} className="ml-auto text-slate-400 hover:text-red-500">
                  <X className="w-3 h-3" />
                </button>
              </li>
            ))}
          </ul>
        )}

        {errors.map((e, i) => (
          <p key={i} className="text-xs text-red-500 flex items-center gap-1">
            <AlertCircle className="w-3 h-3" /> {e}
          </p>
        ))}
      </div>
    );
  }

  // Full drop-zone mode (used in Files tab)
  return (
    <div className="space-y-3">
      <input
        ref={inputRef}
        type="file"
        multiple
        className="hidden"
        onChange={(e) => handleFiles(e.target.files)}
      />
      <div
        onDrop={onDrop}
        onDragOver={(e) => e.preventDefault()}
        onClick={() => inputRef.current?.click()}
        className="border-2 border-dashed border-slate-200 rounded-lg p-6 text-center cursor-pointer hover:border-blue-400 hover:bg-blue-50 transition-colors"
      >
        <Upload className="w-8 h-8 mx-auto text-slate-400 mb-2" />
        <p className="text-sm font-medium text-slate-700">Drop files here or click to browse</p>
        <p className="text-xs text-slate-400 mt-1">All file types supported — max 100 MB each</p>
      </div>

      {uploading && (
        <p className="text-xs text-blue-600 animate-pulse">Uploading…</p>
      )}

      {errors.map((e, i) => (
        <p key={i} className="text-xs text-red-500 flex items-center gap-1">
          <AlertCircle className="w-3 h-3" /> {e}
        </p>
      ))}
    </div>
  );
});

export default FileUpload;
